import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { roomPayApi, RoomPayCheckout, RoomPayPaymentResult } from '../../services/roomPayApi';
import { sepayService } from '../../services/sepayService';
import SePayPaymentCard, { SePayUiStatus } from '../sepay/SePayPaymentCard';
import { getTenantPayRoomCode } from '../../utils/tenantPayRoom';

const QR_TIMEOUT_SECONDS = 15 * 60;
const POLL_MS = 3000;

const formatVnd = (amount: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount || 0);

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('vi-VN');
  } catch {
    return value;
  }
};

const InvoiceRow: React.FC<{ label: string; amount: number; highlight?: boolean }> = ({
  label,
  amount,
  highlight,
}) => {
  if (!amount) return null;
  return (
    <div className={`flex items-center justify-between py-2 ${highlight ? 'font-semibold' : ''}`}>
      <span className={highlight ? 'text-black' : 'text-gray-600'}>{label}</span>
      <span className={highlight ? 'text-[#C4A35A] text-lg' : 'text-black'}>{formatVnd(amount)}</span>
    </div>
  );
};

type Props = {
  onUnpaidChange?: (unpaid: boolean) => void;
};

const MobileTenantInvoicePanel: React.FC<Props> = ({ onUnpaidChange }) => {
  const { user } = useAuth();
  const roomCode = getTenantPayRoomCode(user?.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RoomPayCheckout | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<RoomPayPaymentResult | null>(null);
  const [justPaid, setJustPaid] = useState(false);
  const [qrUiStatus, setQrUiStatus] = useState<SePayUiStatus>('waiting');
  const [qrSecondsLeft, setQrSecondsLeft] = useState(QR_TIMEOUT_SECONDS);

  const lastVisibleRefreshAt = useRef(0);

  const loadCheckout = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!roomCode) {
        onUnpaidChange?.(false);
        setError('Không xác định được phòng. Hãy đăng xuất rồi quét lại QR cửa phòng.');
        setLoading(false);
        return;
      }
      try {
        if (!options?.silent) setLoading(true);
        setError(null);
        const checkout = await roomPayApi.getCheckout(roomCode);
        setData(checkout);
        onUnpaidChange?.(checkout.page_status === 'unpaid');
        if (checkout.page_status !== 'unpaid') {
          setPaymentResult(null);
        }
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        setError(
          status === 404
            ? 'Không tìm thấy phòng với mã QR này.'
            : 'Không thể tải hóa đơn. Vui lòng thử lại.',
        );
        onUnpaidChange?.(false);
      } finally {
        if (!options?.silent) setLoading(false);
      }
    },
    [onUnpaidChange, roomCode],
  );

  useEffect(() => {
    void loadCheckout();
    lastVisibleRefreshAt.current = Date.now();
  }, [loadCheckout]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibleRefreshAt.current < 20_000) return;
      lastVisibleRefreshAt.current = now;
      void loadCheckout({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadCheckout]);

  const handlePay = async () => {
    if (!roomCode) return;
    setPayError(null);
    setPayLoading(true);
    try {
      const result = await roomPayApi.createPayment(roomCode);
      setPaymentResult(result);
      setQrUiStatus('waiting');
      setQrSecondsLeft(QR_TIMEOUT_SECONDS);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setPayError(typeof detail === 'string' ? detail : 'Không tạo được thanh toán. Vui lòng thử lại.');
    } finally {
      setPayLoading(false);
    }
  };

  useEffect(() => {
    const code = paymentResult?.order_code || paymentResult?.code;
    if (!code || qrUiStatus !== 'waiting') return;

    const deadline = Date.now() + QR_TIMEOUT_SECONDS * 1000;
    const tick = async () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setQrSecondsLeft(left);
      if (left <= 0) {
        setQrUiStatus('expired');
        return;
      }
      try {
        const status = await sepayService.getOrderStatus(code);
        if (status.status === 'paid') {
          setQrUiStatus('paid');
          setJustPaid(true);
          void loadCheckout({ silent: true });
        } else if (status.status === 'expired' || status.status === 'cancelled') {
          setQrUiStatus('expired');
        }
      } catch {
        // keep waiting
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadCheckout, paymentResult?.code, paymentResult?.order_code, qrUiStatus]);

  if (loading && !data) {
    return (
      <section className="rounded-2xl border border-black/10 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-3 h-9 w-9 animate-spin rounded-full border-2 border-[#C4A35A] border-t-transparent" />
        <p className="text-sm text-black/60">Đang tải hóa đơn...</p>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="rounded-2xl border border-black/10 bg-white p-6 text-center shadow-sm">
        <i className="fas fa-file-invoice-dollar mb-2 text-2xl text-[#C4A35A]" />
        <p className="text-sm text-black/70">{error}</p>
      </section>
    );
  }

  const invoice = data?.invoice;
  const tenant = data?.tenant;
  const room = data?.room;
  const unpaid = data?.page_status === 'unpaid';

  return (
    <section className="space-y-3">
      <div className="px-1">
        <h1 className="text-lg font-semibold text-black">Hóa đơn</h1>
      </div>

      <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#C4A35A]">Thông tin phòng</p>
        <p className="mt-1 text-sm font-medium text-black">
          {room?.room_number ? `Phòng ${room.room_number}` : room?.title || roomCode}
        </p>
        {room?.full_address && <p className="mt-1 text-xs text-black/55">{room.full_address}</p>}
        {tenant?.name && (
          <p className="mt-2 text-xs text-emerald-800">
            <i className="fas fa-user-check mr-1" />
            Người thuê: {tenant.name}
          </p>
        )}
      </div>

      {justPaid && !unpaid && (
        <p className="rounded-2xl bg-green-50 px-3 py-2 text-sm text-green-800">
          Thanh toán thành công. Hệ thống sẽ cập nhật hóa đơn trong giây lát.
        </p>
      )}

      <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-black">
          {unpaid ? 'Hóa đơn cần thanh toán' : 'Không có hóa đơn chưa thanh toán'}
        </h2>
        <p className="mb-4 mt-1 text-xs text-black/50">
          {unpaid
            ? 'Kiểm tra thông tin và bấm Thanh toán để hiện QR chuyển khoản.'
            : 'Phòng này hiện không có khoản nào cần thanh toán.'}
        </p>

        {invoice ? (
          <>
            {(invoice.billing_period_start || invoice.due_date) && (
              <div className="mb-4 space-y-1 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                {invoice.billing_period_start && invoice.billing_period_end && (
                  <p>
                    Kỳ: {formatDate(invoice.billing_period_start)} – {formatDate(invoice.billing_period_end)}
                  </p>
                )}
                {invoice.due_date && <p>Hạn thanh toán: {formatDate(invoice.due_date)}</p>}
              </div>
            )}

            <div className="divide-y divide-gray-100">
              <InvoiceRow label="Tiền thuê" amount={invoice.rent} />
              <InvoiceRow label="Điện" amount={invoice.electricity} />
              <InvoiceRow label="Nước" amount={invoice.water} />
              <InvoiceRow label="Dịch vụ" amount={invoice.services} />
              <InvoiceRow label="Khoản khác" amount={invoice.other} />
            </div>

            <div className="mt-4 border-t-2 border-[#C4A35A]/30 pt-4">
              <InvoiceRow label="Tổng tiền" amount={invoice.total} highlight />
            </div>

            {!paymentResult ? (
              <>
                <button
                  type="button"
                  onClick={() => void handlePay()}
                  disabled={payLoading || !unpaid}
                  className="mt-4 h-11 w-full rounded-lg bg-[#C4A35A] text-sm font-semibold text-white transition-colors hover:bg-[#9A7B2E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {payLoading ? 'Đang tạo thanh toán...' : 'Thanh toán'}
                </button>
                {payError && <p className="mt-2 text-center text-xs text-red-600">{payError}</p>}
              </>
            ) : (
              <div className="mt-4">
                <SePayPaymentCard
                  title="Thanh toán hóa đơn phòng"
                  amount={paymentResult.amount}
                  qrUrl={paymentResult.qrUrl || paymentResult.qrCode || paymentResult.qr_code_url}
                  bank={paymentResult.bank}
                  accountNumber={paymentResult.accountNumber || paymentResult.account_number}
                  accountName={paymentResult.account_name}
                  code={paymentResult.order_code || paymentResult.code || ''}
                  status={qrUiStatus}
                  secondsLeft={qrSecondsLeft}
                  successMessage="Thanh toán thành công. Hệ thống sẽ cập nhật hóa đơn trong giây lát."
                />
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg bg-gray-50 px-4 py-6 text-center">
            <p className="text-sm text-gray-600">
              Tất cả hóa đơn đã được thanh toán hoặc chưa phát sinh khoản mới.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MobileTenantInvoicePanel;
